import { AxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from 'react-query';
import {
  deleteEditorFromStory,
  fetchWriter,
  searchWriters,
  fetchWriterStories,
  fetchWriterContracts,
  fetchWriterTransferLinks,
  updateWriterPermission,
} from '../../services/api/Writer';
import { IPaginationResponseDto, IStory, IWriter, IWriterLink, IWriterPermission, ITransferContractByWriter } from '../../services/types';
import useBackdrop from '../useBackdrop';
import useSnackbar from '../useSnackbar';

enum QueryKey {
  AllWriters = 'allWriters',
  Writer = 'Writer',
  WriterStories = 'WriterStories',
  WriterTransferLinks = 'WriterTransferLinks',
  WriterContracts = 'WriterContracts',
}

interface IUseWriterMutation {
  update: UseMutationResult<IWriter, Error, Partial<IWriterPermission>>;
  removeEditorFromStory: UseMutationResult<unknown, Error, { storyId: number; editorId: number }>;
}

export const useWriters = (keyword?: string): UseQueryResult<IPaginationResponseDto<IWriter> | undefined, AxiosError> =>
  useQuery<IPaginationResponseDto<IWriter> | undefined, AxiosError>(
    [QueryKey.AllWriters, { keyword }],
    async () => {
      if (!keyword) {
        return;
      }

      try {
        const [fetchPromise] = searchWriters(keyword);
        const result = await fetchPromise;

        if (result.status !== 200) {
          throw new Error('API Error');
        }

        return result.data;
      } catch (e) {
        throw e;
      }
    },
    { keepPreviousData: true },
  );

export const useWriter = (writerId?: number): UseQueryResult<IWriter | undefined, AxiosError> =>
  useQuery<IWriter | undefined, AxiosError>([QueryKey.Writer, writerId], async () => {
    try {
      if (!writerId) {
        return;
      }

      const [fetchPromise] = fetchWriter(writerId);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    } catch (e) {
      throw e;
    }
  });

export const useWriterStories = (writerId?: number): UseQueryResult<IPaginationResponseDto<IStory> | undefined, AxiosError> =>
  useQuery<IPaginationResponseDto<IStory> | undefined, AxiosError>([QueryKey.WriterStories, writerId], async () => {
    if (!writerId) {
      return;
    }

    try {
      const [fetchPromise] = fetchWriterStories(writerId);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    } catch (e) {
      throw e;
    }
  });

export const useWriterContracts = (writerId?: number): UseQueryResult<ITransferContractByWriter[] | undefined, AxiosError> =>
  useQuery<ITransferContractByWriter[] | undefined, AxiosError>([QueryKey.WriterContracts, writerId], async () => {
    if (!writerId) {
      return;
    }

    try {
      const [fetchPromise] = fetchWriterContracts(writerId);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    } catch (e) {
      throw e;
    }
  });

export const useWriterTransferLinks = (writerId?: number): UseQueryResult<IWriterLink[] | undefined, AxiosError> =>
  useQuery<IWriterLink[] | undefined, AxiosError>([QueryKey.WriterTransferLinks, writerId], async () => {
    if (!writerId) {
      return;
    }

    try {
      const [fetchPromise] = fetchWriterTransferLinks(writerId);
      const result = await fetchPromise;

      if (result.status !== 200) {
        throw new Error('API Error');
      }

      return result.data;
    } catch (e) {
      throw e;
    }
  });

export const useWriterMutation = (): IUseWriterMutation => {
  const snackbar = useSnackbar();
  const backdrop = useBackdrop();
  const queryClient = useQueryClient();

  const update = useMutation<IWriter, AxiosError, Partial<IWriterPermission>>(
    async (permission: Partial<IWriterPermission>) => {
      if (!permission.id || !permission.userId) {
        throw new Error('id is necessary to update');
      }

      backdrop.show();
      const { data: apiResult } = await updateWriterPermission(permission.userId, permission);

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult.data;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Updated successfully.');
        queryClient.invalidateQueries(QueryKey.AllWriters);
        queryClient.setQueryData([QueryKey.Writer, variables.id], data);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  const removeEditorFromStory = useMutation<unknown, AxiosError, { storyId: number; editorId: number }>(
    async ({ storyId, editorId }) => {
      backdrop.show();
      const { data: apiResult } = await deleteEditorFromStory(storyId, editorId);

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      return apiResult;
    },
    {
      onSuccess: (data, variables) => {
        snackbar.success('Deleted successfully.');
        queryClient.invalidateQueries([QueryKey.WriterStories, variables.editorId]);
      },
      onError: e => {
        snackbar.error(e);
      },
      onSettled: () => {
        backdrop.hide();
      },
    },
  );

  return {
    update,
    removeEditorFromStory,
  };
};
